import classNames from 'classnames'
import * as React from 'react'
import type * as Api from 'src/api'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import * as Table from 'src/tailwind/components/Table'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import ProgressBar from 'src/views/components/ProgressBar'
import GraduatesExportButton from './GraduatesExportButton'
import { isCoursesType, rowToCells, type GraduatesFilters } from './query'
import { useGraduatesSearch } from './useGraduatesSearch'

interface Props {
  readonly filters: GraduatesFilters
  readonly page: number
  readonly perPage: number
}

export default function GraduatesResults({ filters, page, perPage }: Props): JSX.Element {
  const t = useTranslatable()
  const search = useGraduatesSearch(filters, page, perPage)

  switch (search.phase) {
    case 'starting':
      return <SearchProgress progress={0} />
    case 'pending':
      return <SearchProgress progress={search.progress} />
    case 'error':
      return (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-card bg-lightRed px-4 py-3 text-primaryRed">
          <div>
            {search.messages.length > 0
              ? search.messages.map((message) => <div key={message}>{message}</div>)
              : t('error:an_error_occurred')}
          </div>
          <Button variant="red" type="button" onClick={search.retry}>
            {t('statistics:try_again')}
          </Button>
        </div>
      )
    case 'done':
      return search.rows == null ? (
        <SearchProgress progress={100} />
      ) : (
        <ResultsTable result={search.rows} isLoading={search.isLoadingRows} filters={filters} />
      )
  }
}

function SearchProgress({ progress }: { readonly progress: number }): JSX.Element {
  const t = useTranslatable()

  return (
    <ProgressBar
      label={t('statistics:search_in_progress')}
      percent={progress}
      className="mx-auto mb-20 mt-10 max-w-xl"
    />
  )
}

interface ResultsTableProps {
  readonly result: Api.getAdministratorStatisticsGraduatesSearchRowsOk
  readonly isLoading: boolean
  readonly filters: GraduatesFilters
}

function ResultsTable({ result, isLoading, filters }: ResultsTableProps): JSX.Element {
  const t = useTranslatable()
  const { type } = filters
  const rows = React.useMemo(() => result.data.map(rowToCells), [result])

  React.useEffect(() => {
    if (import.meta.env.DEV && rows.length > 0 && rows[0]!.length !== result.meta.headers.length) {
      console.warn(
        `Graduates statistics: ${rows[0]!.length} cells per row, ${result.meta.headers.length} headers`,
        result.data[0]
      )
    }
  }, [result, rows])

  // Paginated by students, not rows: course types return every course of the page's students
  const pagination = React.useMemo(
    (): Api.Pagination => ({
      total: result.meta.total,
      count: result.data.length,
      perPage: result.meta.perPage,
      currentPage: result.meta.currentPage,
      totalPages: result.meta.lastPage,
      links: { next: null, previous: null },
    }),
    [result]
  )

  if (result.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  return (
    <div className="relative mb-7">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-20">
          <Loader />
        </div>
      )}

      {isCoursesType(type) && (
        <div className="mt-3 text-primaryTextColor" data-testid="graduates-counts">
          {result.meta.total.toLocaleString()} {t('statistics:students_count')} /{' '}
          {result.meta.rowsCount.toLocaleString()} {t('statistics:rows_count')}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <PaginationTop pagination={pagination} showTotalCount={!isCoursesType(type)} />
        </div>
        <GraduatesExportButton filters={filters} />
      </div>

      <Table.Table className={classNames('mt-7', isLoading && 'opacity-60')}>
        <Table.Thead>
          <Table.Tr>
            {result.meta.headers.map((header, index) => (
              <Table.Th key={index} className="whitespace-nowrap">
                {header}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {rows.map((cells, rowIndex) => (
            <Table.Tr key={rowIndex}>
              {cells.map((cell, cellIndex) => (
                <Table.Td key={cellIndex}>{cell}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table.Table>

      <PaginationBottom pagination={pagination} />
    </div>
  )
}
